import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Gauge, UtensilsCrossed, ListOrdered, Sparkles } from "lucide-react";

const alertStyle = (a) => {
  if (a.startsWith("[ALERTE PAUSE") || a.startsWith("[INCOMPATIBILITÉ"))
    return "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300";
  if (a.startsWith("[ALERTE DÉPASSEMENT"))
    return "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300";
  return "border-orange-500/40 bg-orange-500/10 text-orange-800 dark:text-orange-300";
};

const Kpi = ({ label, value, suffix, testid }) => (
  <div className="rounded-md border border-border bg-secondary/40 px-3 py-2" data-testid={testid}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="font-display font-bold text-lg tabular-nums">{value ?? "—"}{value != null ? suffix : ""}</p>
  </div>
);

export function RegulReport({ report }) {
  if (!report || (!report.kpis && !report.ai)) return null;
  const k = report.kpis || {};
  const alerts = report.alerts || [];
  const meals = report.meal_breaks || [];
  const chrono = report.chronogram || [];
  const ai = report.ai;
  return (
    <Card className="p-4 rounded-md shadow-none space-y-4" data-testid="regul-report">
      <h2 className="font-display font-semibold text-sm flex items-center gap-2">
        <Gauge className="h-4 w-4 text-primary" /> Rapport de régulation — alertes & indicateurs
      </h2>

      {report.kpis && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="regul-report-kpis">
        <Kpi label="Km à vide économisés" value={k.empty_km_saved} suffix=" km" testid="regul-kpi-saved" />
        <Kpi label="Km à vide restants" value={k.empty_km_total} suffix=" km" testid="regul-kpi-empty" />
        <Kpi label="Indice d'équité chauffeurs" value={k.equity_pct} suffix=" %" testid="regul-kpi-equity" />
        <Kpi label="Occupation VSL / Taxi" value={k.occupancy_pct} suffix=" %" testid="regul-kpi-occupancy" />
      </div>
      )}

      {ai && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="regul-report-ai">
          <div className="text-xs font-semibold flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Analyse IA du planning
            <Badge variant="outline" className="text-[10px] border-primary/50 text-primary">{ai.model || "Claude"}</Badge>
          </div>
          {ai.resume && <p className="text-xs text-muted-foreground" data-testid="regul-ai-resume">{ai.resume}</p>}
          {(ai.suggestions || []).map((s, i) => (
            <div key={s.titre || i} className="text-xs rounded-md bg-background/70 border border-border px-3 py-2" data-testid={`regul-ai-suggestion-${i}`}>
              <span className="font-semibold">{s.titre}</span>
              <span className="block text-muted-foreground mt-0.5">{s.detail}</span>
              {s.impact && <span className="block mt-0.5 text-primary">Gain estimé : {s.impact}</span>}
            </div>
          ))}
          {(ai.risques || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {ai.risques.map((r) => (
                <Badge key={r} variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 dark:text-amber-300">⚠ {r}</Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {alerts.length > 0 ? (
        <div className="space-y-1.5" data-testid="regul-report-alerts">
          {alerts.map((a, i) => (
            <div key={a} className={`flex items-start gap-2 text-xs rounded-md border px-3 py-2 ${alertStyle(a)}`} data-testid={`regul-alert-${i}`}>
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      ) : (
        report.kpis && <p className="text-xs text-muted-foreground" data-testid="regul-report-no-alerts">Aucune alerte régulateur ✓</p>
      )}

      {meals.length > 0 && (
        <div data-testid="regul-report-meals">
          <p className="text-xs font-medium flex items-center gap-1.5 mb-1.5">
            <UtensilsCrossed className="h-3.5 w-3.5 text-primary" /> Pauses repas planifiées (véhicule verrouillé)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {meals.map((m) => (
              <Badge key={m.driver_id} variant="outline" className="text-[11px]" data-testid={`regul-meal-${m.driver_id}`}>
                {m.driver} · {m.start}–{m.end}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {chrono.length > 0 && (
        <details data-testid="regul-report-chrono">
          <summary className="text-xs font-medium cursor-pointer flex items-center gap-1.5">
            <ListOrdered className="h-3.5 w-3.5 text-primary" /> Chronogramme détaillé ({chrono.length} tournée(s))
          </summary>
          <div className="mt-2 space-y-1.5 max-h-72 overflow-auto">
            {chrono.map((c) => (
              <div key={`${c.time}-${c.vehicle}`} className="text-[11px] rounded-md bg-secondary/60 px-3 py-2">
                <span className="font-semibold">{c.time} · {c.vehicle}</span>
                {c.shared && <Badge variant="outline" className="ml-1.5 text-[10px] border-primary text-primary">Groupé ×{(c.patients || []).length}</Badge>}
                <span className="block text-muted-foreground">{(c.patients || []).join(", ")} — 👤 {(c.drivers || []).join(", ")}</span>
                <span className="block mt-0.5">{c.steps}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}
